import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import {
  Plus, KeyRound, UserX, UserCheck, Truck, Star, CheckCircle2, Ban, Unlock,
  FileWarning, ClipboardEdit, StickyNote, History, XCircle,
} from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

const emptyForm = { nome: "", cpf: "", telefone: "", whatsapp: "", email: "", pix: "", login: "", senha: "" };

const PORTE_LABEL = { pequeno: "Pequeno", medio: "Médio", grande: "Grande" };
const FOTO_LABEL = { cnh: "CNH", crlv: "CRLV", frente: "Frente", traseira: "Traseira", lateral_esq: "Lateral E", lateral_dir: "Lateral D", bau_aberto: "Baú aberto" };
const ONBOARDING_LABEL = {
  aguardando_senha: "1º ACESSO PENDENTE",
  aguardando_vistoria: "VISTORIA PENDENTE",
  aguardando_aprovacao: "AGUARDANDO APROVAÇÃO",
  alteracao_pendente: "ALTERAÇÃO AGUARDANDO APROVAÇÃO",
  correcao_solicitada: "CORREÇÃO SOLICITADA",
  reprovado: "REPROVADO",
  ativo: "ATIVO",
};
const STATUS_STYLE = {
  ativo: "bg-emerald-100 text-emerald-700",
  aguardando_aprovacao: "bg-amber-100 text-amber-700",
  alteracao_pendente: "bg-amber-100 text-amber-700",
  correcao_solicitada: "bg-blue-100 text-blue-700",
  reprovado: "bg-red-100 text-red-600",
};

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "-");

function Motoristas({ user }) {
  const [params] = useSearchParams();
  const filtro = params.get("f") || "";
  const [list, setList] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bloqueioTarget, setBloqueioTarget] = useState(null);
  const [blTipo, setBlTipo] = useState("temporario");
  const [blMotivo, setBlMotivo] = useState("");
  const [blDias, setBlDias] = useState(3);
  const [detalhe, setDetalhe] = useState(null);

  const load = () => {
    api.get("/admin/motoristas").then((r) => setList(r.data.motoristas)).catch(() => setList([]));
    api.get("/admin/ranking").then((r) => setRanking(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.nome || !form.login || !form.senha) return toast.error("Nome, login e senha são obrigatórios");
    setSaving(true);
    try {
      await api.post("/admin/motoristas", form);
      toast.success("Motorista cadastrado com senha temporária!");
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  const aprovar = async (d) => {
    if (!window.confirm(`Aprovar e ativar ${d.nome}?`)) return;
    try {
      await api.post(`/admin/motoristas/${d.driver_id}/aprovar`);
      toast.success("Motorista aprovado e ativado!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao aprovar"); }
  };

  const reprovar = async (d) => {
    const motivo = window.prompt(`Motivo da reprovação de ${d.nome}:`);
    if (!motivo) return;
    try {
      await api.post(`/admin/motoristas/${d.driver_id}/reprovar`, { motivo });
      toast.success("Cadastro reprovado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const correcao = async (d) => {
    const mensagem = window.prompt(`Descreva a correção necessária para ${d.nome}:`);
    if (!mensagem) return;
    try {
      await api.post(`/admin/motoristas/${d.driver_id}/solicitar-correcao`, { mensagem });
      toast.success("Correção solicitada — motorista será notificado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const bloquear = async () => {
    if (!blMotivo.trim()) return toast.error("Informe o motivo do bloqueio");
    try {
      await api.post(`/admin/motoristas/${bloqueioTarget.driver_id}/bloquear`, {
        tipo: blTipo, motivo: blMotivo, dias: blTipo === "prazo" ? Number(blDias) : null,
      });
      toast.success("Motorista bloqueado");
      setBloqueioTarget(null); setBlMotivo(""); setBlTipo("temporario");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao bloquear"); }
  };

  const desbloquear = async (d) => {
    const motivo = window.prompt(`Motivo/observação do desbloqueio de ${d.nome}:`) || "";
    try {
      await api.post(`/admin/motoristas/${d.driver_id}/desbloquear`, { motivo });
      toast.success("Motorista desbloqueado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao desbloquear"); }
  };

  const observacao = async (d) => {
    const texto = window.prompt(`Observação interna sobre ${d.nome}:`);
    if (!texto) return;
    try {
      await api.post(`/admin/motoristas/${d.driver_id}/observacao`, { texto });
      toast.success("Observação registrada");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const verDetalhe = async (d) => {
    if (detalhe?.driver_id === d.driver_id) return setDetalhe(null);
    try {
      const r = await api.get(`/admin/motoristas/${d.driver_id}`);
      setDetalhe(r.data);
    } catch { toast.error("Falha ao carregar detalhes"); }
  };

  const resetSenha = async (d) => {
    const senha = window.prompt(`Nova senha para ${d.nome}:`);
    if (!senha) return;
    try {
      await api.patch(`/admin/motoristas/${d.driver_id}`, { senha });
      toast.success("Senha redefinida!");
    } catch { toast.error("Falha ao redefinir senha"); }
  };

  const editField = async (d, field, label) => {
    const v = window.prompt(`${label}:`, d[field] || "");
    if (v === null) return;
    try {
      await api.patch(`/admin/motoristas/${d.driver_id}`, { [field]: v });
      toast.success("Atualizado!");
      load();
    } catch { toast.error("Falha ao atualizar"); }
  };

  const lista = (list || []).filter((d) => {
    if (filtro === "aprovacao") return ["aguardando_aprovacao", "alteracao_pendente"].includes(d.onboarding_status);
    if (filtro === "bloqueados") return !!d.bloqueio;
    return true;
  });

  const notaChip = (av) => av && (
    <span className="text-[11px] font-bold text-navy bg-gold-light border border-[#F5B301]/60 px-2 py-0.5 rounded-full" data-testid="driver-nota-chip">
      ★ {av.nota.toFixed(1)}{av.total > 0 ? ` (${av.total})` : ""}
    </span>
  );

  return (
    <AdminShell user={user} title="Motoristas / Parceiros">
      <div className="px-4 mt-4 space-y-3" data-testid="drivers-page">
        <div className="flex gap-1.5" data-testid="drivers-filtros">
          {[["", "Todos"], ["aprovacao", "Aguardando aprovação"], ["bloqueados", "Bloqueados"]].map(([f, l]) => (
            <a key={f} href={`/admin/motoristas${f ? `?f=${f}` : ""}`} data-testid={`filtro-${f || "todos"}`}
              className={`px-3 py-1.5 rounded-full text-[10px] font-bold ${filtro === f ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"}`}>
              {l}
            </a>
          ))}
        </div>

        <button data-testid="new-driver-btn" onClick={() => setShowForm(!showForm)}
          className="w-full py-3.5 rounded-xl bg-navy text-[#F5B301] font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Plus className="w-4 h-4" /> CADASTRAR MOTORISTA
        </button>

        {showForm && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2.5" data-testid="driver-form">
            {[["nome", "Nome completo *"], ["cpf", "CPF"], ["telefone", "Telefone"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["pix", "Chave Pix / dados bancários"], ["login", "Login (telefone ou e-mail) *"], ["senha", "Senha temporária *"]].map(([k, ph]) => (
              <input key={k} data-testid={`driver-field-${k}`} className={inputCls} placeholder={ph}
                value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            ))}
            <p className="text-[11px] text-slate-400">No primeiro acesso, o motorista criará a senha definitiva e enviará a vistoria do veículo (CNH + 5 fotos) para sua aprovação.</p>
            <button data-testid="save-driver-btn" onClick={create} disabled={saving}
              className="w-full py-3.5 rounded-xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-60">
              {saving ? "Salvando..." : "SALVAR MOTORISTA"}
            </button>
          </div>
        )}

        {list === null && <div className="flex justify-center py-14"><div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" /></div>}
        {lista.length === 0 && list !== null && <p className="text-center text-sm text-slate-400 py-14" data-testid="drivers-empty">Nenhum motorista neste filtro.</p>}
        {lista.map((d) => (
          <div key={d.driver_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`driver-card-${d.driver_id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-navy flex items-center justify-center overflow-hidden shrink-0" data-testid={`driver-foto-${d.driver_id}`}>
                  {d.foto_perfil
                    ? <img src={`${API}/files/${d.foto_perfil}`} alt={d.nome} className="w-full h-full object-cover" />
                    : <Truck className="w-4 h-4 text-[#F5B301]" />}
                </div>
                <div>
                  <p className="font-heading font-bold text-navy text-sm">{d.nome}</p>
                  <p className="text-[11px] text-slate-400">login: {d.login}{d.placa ? ` · ${d.placa}` : ""}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {notaChip(d.avaliacao)}
                {d.bloqueio ? (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid={`driver-status-${d.driver_id}`}>
                    BLOQUEADO{d.bloqueio.tipo === "prazo" && d.bloqueio.ate ? ` até ${new Date(d.bloqueio.ate).toLocaleDateString("pt-BR")}` : ""}
                  </span>
                ) : (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[d.onboarding_status] || "bg-slate-200 text-slate-500"}`} data-testid={`driver-status-${d.driver_id}`}>
                    {d.onboarding_status === "ativo" && !d.ativo ? "INATIVO" : ONBOARDING_LABEL[d.onboarding_status] || "ATIVO"}
                  </span>
                )}
              </div>
            </div>
            {d.bloqueio && (
              <p className="text-[11px] text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5 mb-1.5" data-testid={`driver-bloqueio-info-${d.driver_id}`}>
                🚫 {d.bloqueio.tipo} · Motivo: {d.bloqueio.motivo} · por {d.bloqueio.por} em {fmtDt(d.bloqueio.em)}
              </p>
            )}
            {d.onboarding_status === "correcao_solicitada" && d.correcao && (
              <p className="text-[11px] text-blue-700 bg-blue-50 rounded-lg px-2.5 py-1.5 mb-1.5">📝 Correção: {d.correcao.mensagem}</p>
            )}
            <p className="text-xs text-slate-600">{d.telefone || "-"} {d.whatsapp ? `· WA: ${d.whatsapp}` : ""} {d.pix ? `· Pix: ${d.pix}` : ""}</p>
            {(d.portes || []).length > 0 && (
              <div className="flex gap-1 mt-1.5" data-testid={`driver-portes-${d.driver_id}`}>
                {d.portes.map((p) => (
                  <span key={p} className="text-[10px] font-bold bg-navy text-[#F5B301] px-2 py-0.5 rounded-full">{PORTE_LABEL[p]}</span>
                ))}
              </div>
            )}
            {d.vistoria && Object.keys(d.vistoria).length > 0 && (
              <div className="grid grid-cols-3 gap-1.5 mt-2.5" data-testid={`driver-vistoria-${d.driver_id}`}>
                {Object.entries(FOTO_LABEL).map(([k, label]) => d.vistoria[k] && (
                  <a key={k} href={`${API}/files/${d.vistoria[k]}`} target="_blank" rel="noopener noreferrer" data-testid={`vistoria-img-${k}`}>
                    {String(d.vistoria[k]).endsWith(".pdf") ? (
                      <div className="w-full h-16 rounded-lg border border-slate-200 bg-slate-50 flex flex-col items-center justify-center">
                        <span className="text-lg">📄</span>
                        <span className="text-[9px] font-bold text-navy">PDF</span>
                      </div>
                    ) : (
                      <img src={`${API}/files/${d.vistoria[k]}`} alt={label} className="w-full h-16 object-cover rounded-lg border border-slate-200" />
                    )}
                    <p className="text-[9px] text-center text-slate-400 mt-0.5">{label}</p>
                  </a>
                ))}
              </div>
            )}

            {detalhe?.driver_id === d.driver_id && (
              <div className="mt-3 border-t border-slate-100 pt-3 space-y-2.5" data-testid={`driver-detalhe-${d.driver_id}`}>
                <p className="text-[11px] text-slate-500">
                  CPF: {d.cpf || "-"} · E-mail: {d.email || "-"}<br />
                  Corridas: {detalhe.corridas_total} total · {detalhe.corridas_concluidas} concluídas · Termo aceito: v{detalhe.termo_versao_aceita || "-"}
                </p>
                {(detalhe.observacoes_internas || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Observações internas</p>
                    {detalhe.observacoes_internas.map((o, i) => (
                      <p key={i} className="text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5 mb-1">🗒️ {o.texto} <span className="text-slate-400">— {o.por}, {fmtDt(o.em)}</span></p>
                    ))}
                  </div>
                )}
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><History className="w-3 h-3" /> Histórico do ADM</p>
                  {(detalhe.adm_history || []).length === 0 && <p className="text-[11px] text-slate-400">Sem ações registradas.</p>}
                  {(detalhe.adm_history || []).slice().reverse().map((h, i) => (
                    <p key={i} className="text-[11px] text-slate-600 border-l-2 border-[#F5B301] pl-2 mb-1">{h.detalhe} <span className="text-slate-400">— {h.por}, {fmtDt(h.em)}</span></p>
                  ))}
                </div>
                {(detalhe.termo_aceites || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Aceites do termo</p>
                    {detalhe.termo_aceites.map((a) => (
                      <p key={a.id} className="text-[11px] text-slate-500">v{a.versao} · {fmtDt(a.em)}{a.ip ? ` · IP ${a.ip}` : ""}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-1.5 mt-2.5 flex-wrap">
              {(d.onboarding_status === "aguardando_aprovacao" || d.onboarding_status === "alteracao_pendente") && (
                <button data-testid={`aprovar-driver-${d.driver_id}`} onClick={() => aprovar(d)}
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-bold flex items-center gap-1 active:scale-95 transition-transform">
                  <CheckCircle2 className="w-3.5 h-3.5" /> APROVAR
                </button>
              )}
              {d.onboarding_status === "alteracao_pendente" && d.alteracao && (
                <p className="w-full text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid={`driver-alteracao-${d.driver_id}`}>
                  📝 Alterou: {d.alteracao.campos.join(", ")} — {new Date(d.alteracao.em).toLocaleString("pt-BR")}
                </p>
              )}
              {(d.onboarding_status === "aguardando_aprovacao" || d.onboarding_status === "correcao_solicitada") && (
                <>
                  <button data-testid={`reprovar-driver-${d.driver_id}`} onClick={() => reprovar(d)}
                    className="px-3 py-2 rounded-lg bg-red-600 text-white text-[10px] font-bold flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> REPROVAR
                  </button>
                  <button data-testid={`correcao-driver-${d.driver_id}`} onClick={() => correcao(d)}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white text-[10px] font-bold flex items-center gap-1">
                    <ClipboardEdit className="w-3.5 h-3.5" /> SOLICITAR CORREÇÃO
                  </button>
                </>
              )}
              {d.bloqueio ? (
                <button data-testid={`desbloquear-driver-${d.driver_id}`} onClick={() => desbloquear(d)}
                  className="px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                  <Unlock className="w-3.5 h-3.5" /> DESBLOQUEAR
                </button>
              ) : d.onboarding_status === "ativo" && (
                <button data-testid={`bloquear-driver-${d.driver_id}`} onClick={() => { setBloqueioTarget(d); setBlMotivo(""); }}
                  className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-bold flex items-center gap-1">
                  <Ban className="w-3.5 h-3.5" /> BLOQUEAR
                </button>
              )}
              <button data-testid={`detalhe-driver-${d.driver_id}`} onClick={() => verDetalhe(d)}
                className="px-2.5 py-1.5 rounded-lg bg-navy text-[#F5B301] text-[10px] font-bold">
                {detalhe?.driver_id === d.driver_id ? "FECHAR" : "DETALHES"}
              </button>
              <button data-testid={`obs-driver-${d.driver_id}`} onClick={() => observacao(d)}
                className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600 flex items-center gap-1"><StickyNote className="w-3 h-3" /> OBS</button>
              <button data-testid={`driver-edit-${d.driver_id}`} onClick={() => editField(d, "telefone", "Telefone")} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600">TEL</button>
              <button data-testid={`driver-pix-${d.driver_id}`} onClick={() => editField(d, "pix", "Chave Pix")} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600">PIX</button>
              <button data-testid={`driver-reset-${d.driver_id}`} onClick={() => resetSenha(d)} className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600 flex items-center gap-1"><KeyRound className="w-3 h-3" /> SENHA</button>
            </div>
          </div>
        ))}

        {ranking && !filtro && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mt-4" data-testid="ranking-section">
            <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-3">⭐ Ranking de notas</h3>
            {ranking.motoristas.map((m) => (
              <div key={m.driver_id} className="py-1.5 border-b border-slate-50 last:border-0" data-testid={`rank-motorista-${m.driver_id}`}>
                <div className="flex justify-between text-xs">
                  <span className="font-semibold text-navy">{m.nome}</span>
                  <span className="font-bold text-navy flex items-center gap-1"><Star className="w-3 h-3 fill-[#F5B301] text-[#F5B301]" />{m.nota.toFixed(1)} ({m.total})</span>
                </div>
              </div>
            ))}
            {ranking.motoristas.length === 0 && <p className="text-xs text-slate-400">Sem motoristas.</p>}
          </div>
        )}
      </div>

      {bloqueioTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="bloqueio-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setBloqueioTarget(null)} />
          <div className="relative bg-white rounded-2xl p-5 w-full max-w-sm space-y-3">
            <p className="font-heading font-extrabold text-navy text-sm flex items-center gap-2"><Ban className="w-4 h-4 text-red-500" /> Bloquear {bloqueioTarget.nome}</p>
            <p className="text-[11px] font-bold text-slate-400 uppercase">Tipo de bloqueio</p>
            <div className="grid grid-cols-3 gap-1.5">
              {[["temporario", "Temporário"], ["prazo", "Com prazo"], ["indefinido", "Indefinido"]].map(([v, l]) => (
                <button key={v} data-testid={`bloqueio-tipo-${v}`} onClick={() => setBlTipo(v)}
                  className={`py-2.5 rounded-xl border-2 text-[10px] font-bold ${blTipo === v ? "border-red-500 bg-red-50 text-red-600" : "border-slate-200 text-slate-500"}`}>
                  {l}
                </button>
              ))}
            </div>
            {blTipo === "prazo" && (
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Prazo (dias)</label>
                <input data-testid="bloqueio-dias" type="number" min="1" className={inputCls} value={blDias} onChange={(e) => setBlDias(e.target.value)} />
              </div>
            )}
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Motivo do bloqueio *</label>
              <textarea data-testid="bloqueio-motivo" rows={3} className={inputCls} value={blMotivo} onChange={(e) => setBlMotivo(e.target.value)} placeholder="Descreva o motivo..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="bloqueio-confirmar" onClick={bloquear}
                className="py-3 rounded-xl bg-red-600 text-white font-heading font-extrabold text-xs">BLOQUEAR</button>
              <button data-testid="bloqueio-cancelar" onClick={() => setBloqueioTarget(null)}
                className="py-3 rounded-xl bg-slate-100 text-slate-500 font-heading font-bold text-xs">CANCELAR</button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

export default function AdminDrivers() {
  return <AdminGate>{(user) => <Motoristas user={user} />}</AdminGate>;
}
